// PageCard.tsx
import React from 'react';
import styled from 'styled-components';
import { lightTheme, Theme } from '../../../../utils/themes';
import StyledText from '../../../../components/StyledComponents/StyledText';
import TooltipIcon from '../../../../assets/icons/Tooltip';
import OpitonsIcon from '../../../../assets/icons/OptionsIcon';
import Badge from '../../../../components/Badge';
import { hexToRgb } from '@mui/material';
import { capitalizeFirstLetter, formatDateDefault, formattedText, hexToRgba, TextWithoutDashes } from '../../../../utils/helper';
import moment from 'moment';
import { Visibility } from '@mui/icons-material';
import DropdownButton from '../../../../components/StyledComponents/StyledDropdownButton';
import EditIcon from '../../../../assets/icons/EditIcon';
import CopyIcon from '../../../../assets/icons/CopyIcon';
import DeleteIcon from '../../../../assets/icons/DeleteIcon';
import ExternalPlaceholder from '../../../../assets/icons/ExternalPlaceholder';
import ExternalPlaceholderDark from '../../../../assets/icons/ExternalPlaceholderDark';
import { StyledTag } from '../../../../components/StyledTag';
import { createStableElementClass } from '../../../../utils/pageBuilderElementClass';
import { isYoutubeUrl, getYoutubeEmbedUrl } from '../../../../utils/youtubeHelper';
import FormRenderComponent from '../../../../components/PageBuilder/FormRender/FormRenderComponent';
import { SearchQueryRenderer } from '../../../../components/PageBuilder/SearchQueryRenderer';
import PublicDirectory from '../../../../components/PageBuilder/PublicDirectory';
import SummaryCardComponent from '../../../../components/PageBuilder/Summary';
import { STATUS_CODES } from 'http';
import { statusColorMapping } from '../../../../utils/stausMapping';
import { Status } from '../../../../utils/statusHelper';
import { StyledHorizontalLine } from '../../../../components/StyledComponents/Styledhr';
import BuilderZerostatePlaceholder from '../../../../assets/icons/BuilderZerostatePlaceholder';
import WebsiteIcon from '../../../../assets/icons/WebsiteIcon';
import DomainIcon from '../../../../assets/icons/DomainIcon';
import URLIcon from '../../../../assets/icons/URLIcon';

const isVideoFileUrl = (url: string) => /\.(mp4|webm|mov|avi|mkv|m3u8)(\?|#|$)/i.test(url);

// Main Card Container
const CardContainer = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 16px;
//   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.sweetGrey};
  max-width: 600px;
  position: relative;
`;

// Window Header Bar (MacOS style)
const WindowHeader = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 15px;
  border-bottom: 1px solid ${({ theme }) => theme.CorporateTheme.colors.sweetGrey};
`;

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

// Hero Section
const HeroSection = styled.div<{
  $backgroundOverlay?: string;
  $backgroundOverlayOpacity?: number;
  $hasBackgroundImage?: boolean;
  pageType?: string;
}>`
  text-align: center;
  padding: ${({ pageType }) => pageType === 'external' ? '10px 30px' : ' 30px 35px'};
  position: relative;

  ${({ $backgroundOverlay, $backgroundOverlayOpacity, $hasBackgroundImage }) => $hasBackgroundImage && $backgroundOverlay ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${$backgroundOverlay};
      opacity: ${$backgroundOverlayOpacity ?? 1};
      border-radius: inherit;
      pointer-events: none;
      z-index: 1;
    }
  ` : ''}

  > * {
    position: relative;
    z-index: 2;
  }
`;

const ColumnContainer = styled.div<{
  columns: number;
  $backgroundOverlay?: string;
  $backgroundOverlayOpacity?: number;
  $hasBackgroundImage?: boolean;
}>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns}, 1fr)`};
  gap: 10px;
  padding: 30px 35px;
  height: 210px;
  // overflow: auto;
  overflow:hidden;
  position: relative;

  ${({ $backgroundOverlay, $backgroundOverlayOpacity, $hasBackgroundImage }) => $hasBackgroundImage && $backgroundOverlay ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${$backgroundOverlay};
      opacity: ${$backgroundOverlayOpacity ?? 1};
      border-radius: inherit;
      pointer-events: none;
      z-index: 1;
    }
  ` : ''}

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const HeroTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2563eb;
  margin: 0 0 0.5rem 0;
`;

const HeroSubtitle = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
`;

// Button
const LearnMoreButton = styled.button`
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
`;

// Footer Section
const FooterSection = styled.div`
  // display: flex;
  // justify-content: space-between;
  // align-items: center;
  box-shadow: 0px -31px 40px 0px #00000014;
  padding: 20px 25px;
`;

const PageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PageTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const PublishedDate = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
`;

// Badge
const UserBadge = styled.span`
  background-color: #fef3c7;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #f59e0b;
`;

// Three Dots Menu
const Bar = styled.div`
 width: 238px;
 height:7px;
 background: #e5e7eb;
 border-radius: 16px;
`;

const getFontFamilyFromHeaderHtml = (headerHtml: string): string | null => {
  if (!headerHtml || !headerHtml.trim()) {
    return null;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(headerHtml, 'text/html');
    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]'));

    for (const link of links) {
      const href = link.getAttribute('href');
      if (!href) {
        continue;
      }

      try {
        const url = new URL(href, window.location.origin);
        const families = url.searchParams.getAll('family');
        if (families.length > 0) {
          const family = families[0].split(':')[0].replace(/\+/g, ' ');
          return decodeURIComponent(family);
        }
      } catch {
        const match = href.match(/family=([^&]+)/);
        if (match && match[1]) {
          const family = match[1].split(':')[0].replace(/\+/g, ' ');
          return decodeURIComponent(family);
        }
      }
    }

    const styleTags = Array.from(doc.querySelectorAll('style'));
    for (const styleTag of styleTags) {
      const cssText = styleTag.textContent || '';
      const fontFamilyMatch = cssText.match(/font-family\s*:\s*['"]?([^;'"}]+)/i);
      if (fontFamilyMatch && fontFamilyMatch[1]) {
        const family = fontFamilyMatch[1].split(',')[0].trim().replace(/['"]/g, '');
        if (family) {
          return family;
        }
      }
    }

    const scriptTags = Array.from(doc.querySelectorAll('script'));
    for (const scriptTag of scriptTags) {
      const scriptContent = scriptTag.textContent || '';
      const familiesBlockMatch = scriptContent.match(/families\s*:\s*\[([^\]]+)\]/i);
      if (!familiesBlockMatch || !familiesBlockMatch[1]) {
        continue;
      }

      const firstFamilyMatch = familiesBlockMatch[1].match(/['"]([^'"]+)['"]/);
      if (firstFamilyMatch && firstFamilyMatch[1]) {
        const family = firstFamilyMatch[1].split(':')[0].replace(/\+/g, ' ').trim();
        if (family) {
          return decodeURIComponent(family);
        }
      }
    }
  } catch {
    return null;
  }

  return null;
};

interface PageElementConfig {
  elementClass?: string;
  className?: string;
  htmlContent?: string;
  content?: string;
  fontFamily?: string;
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  textAlignment?: string;
  heading?: string;
  paragraphColor?: string;
  paragraphFontFamily?: string;
  paragraphFontSize?: string;
  paragraphFontWeight?: string;
  paragraphTextAlignment?: string;
  backgroundColor?: string;
  separatorColor?: string;
  separatorHeight?: string;
  separatorType?: 'solid' | 'dashed' | string;
  mediaType?: string;
  imageSignedUrl?: string;
  imageUrl?: string;
  imageExternalURL?: string;
  imagePreview?: string;
  imageURL?: string;
  signedUrl?: string;
  videoSignedUrl?: string;
  videoUrl?: string;
  videoExternalURL?: string;
  videoPreview?: string;
  videoURL?: string;
  linkURL?: string;
  openNewTab?: boolean;
  buttonAlignment?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  buttonLink?: string;
  formId?: string;
  formName?: string;
  searchQueryId?: string;
  scope?: string;
  searchQueryName?: string;
  publicDirectorySetting?: {
    scope: string;
    selectedColumns?: string[];
    criteria?: {
      logic: 'AND' | 'OR';
      conditions: Array<{
        field: string;
        operator: string;
        value: any;
      }>;
    };
    queryPayload?: {
      selectedColumns?: string[];
      criteria?: {
        logic: 'AND' | 'OR';
        conditions: Array<{
          field: string;
          operator: string;
          value: any;
        }>;
      };
    };
  };
}

interface PageElement {
  id: number;
  accessKey: string;
  belongsTo?: number;
  position?: number;
  settings?: {
    config?: PageElementConfig;
  };
}

interface PageRow {
  id: number;
  column?: number;
  settings?: {
    bgColor?: string;
    backgroundColor?: string;
    imageSignedUrl?: string;
    imageUrl?: string;
    imagePreview?: string;
    imageOverlayColor?: string;
    imageOverlayOpacity?: string | number;
  };
  elements?: PageElement[];
}

interface PageData {
  id: number;
  name: string;
  rows?: PageRow[];
  statusId: number;
}

interface PageCardProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  pageName?: string;
  publishedDate?: string;
  onEditPage?: () => void;
  onEditPageSetting?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onCopyPageURL?: () => void;
  theme: Theme;
  visibility: string;
  duplicatingPageId: boolean;
  isStandard?: boolean;
  pageData?: PageData;
  customCss?: string;
  customJs?: string;
  headerHtml?: string;
  primaryFont?: string;
  colorSettings?: {
    primaryColor?: string;
    textColor?: string;
    secondaryColor?: string;
  };
  pageType: "internal" | "external";
}

const PageCard: React.FC<PageCardProps> = ({
  title = "Driven By Passion, Powered By Innovation",
  subtitle = "Discover our story, our values and the people behind the creativity.",
  buttonText = "Learn More",
  pageName = "About Us Page",
  publishedDate = "26 March, 2025",
  onEditPage = () => { },
  onEditPageSetting = () => { },
  onDuplicate = () => { },
  onDelete = () => { },
  onCopyPageURL = () => { },
  theme,
  visibility,
  duplicatingPageId,
  isStandard,
  pageData,
  customCss = '',
  customJs = '',
  headerHtml = '',
  primaryFont,
  colorSettings,
  pageType
}) => {

  const styleRef = React.useRef<HTMLStyleElement | null>(null);
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const headerFontFamily = React.useMemo(() => getFontFamilyFromHeaderHtml(headerHtml), [headerHtml]);
  const resolvedFontFamily = headerFontFamily
    ? `${headerFontFamily}, sans-serif`
    : primaryFont
      ? `${primaryFont}, sans-serif`
      : undefined;
  const headingDefaultColor = colorSettings?.primaryColor || '#2563eb';
  const paragraphDefaultColor = colorSettings?.textColor || '#000000';
  const separatorDefaultColor = colorSettings?.secondaryColor || '#BABDC1';
  const separatorDefaultHeight = '2px';
  const buttonDefaultColor = colorSettings?.primaryColor || '#0050ff';
  const buttonDefaultTextColor = '#ffffff';

  const resolveFontWeight = (value?: string) => {
    if (!value) return 400;
    const normalized = String(value).toLowerCase();
    if (normalized === 'bold') return 600;
    if (normalized === 'medium') return 500;
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 400 : parsed;
  };

  const resolveOverlayOpacity = (value?: string | number) => {
    if (typeof value === 'number') {
      return value > 1 ? value / 100 : value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized.endsWith('%')) {
        const parsedPercent = Number(normalized.replace('%', ''));
        return Number.isNaN(parsedPercent) ? 1 : parsedPercent / 100;
      }

      const parsedValue = Number(normalized);
      if (!Number.isNaN(parsedValue)) {
        return parsedValue > 1 ? parsedValue / 100 : parsedValue;
      }
    }

    return 1;
  };

  // Inject custom CSS scoped to this component only
  React.useEffect(() => {
    if (customCss && customCss.trim() && contentWrapperRef.current) {
      try {
        if (styleRef.current) {
          styleRef.current.remove();
        }

        const style = document.createElement('style');
        style.setAttribute('data-custom-css-pagecard', 'true');

        // Scope CSS to only this wrapper
        const scopedCss = customCss
          .split('}')
          .map(rule => {
            if (rule.trim()) {
              const selectorPart = rule.substring(0, rule.lastIndexOf('{'));
              const contentPart = rule.substring(rule.lastIndexOf('{'));
              return `[data-pagecard-content] ${selectorPart}${contentPart}`;
            }
            return rule;
          })
          .join('}');

        style.innerHTML = scopedCss;
        document.head.appendChild(style);
        styleRef.current = style;
      } catch (error) {
        console.error('Error injecting custom CSS:', error);
      }
    }

    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, [customCss]);

  // Do not execute website-level custom JS in admin page cards.
  React.useEffect(() => {
    const existingScript = document.querySelector('script[data-custom-js-pagecard="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    return () => {
      const scriptToRemove = document.querySelector('script[data-custom-js-pagecard="true"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [customJs]);

  // Inject header HTML (meta tags, links, etc.)
  React.useEffect(() => {
    if (headerHtml && headerHtml.trim()) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = headerHtml;

        Array.from(tempDiv.children).forEach((element) => {
          const clone = element.cloneNode(true) as HTMLElement;

          if (clone.tagName.toLowerCase() === 'script') {
            // Skip script execution in admin preview surfaces.
            return;
          }

          clone.setAttribute('data-custom-header-pagecard', 'true');
          document.head.appendChild(clone);
        });

        return () => {
          const customHeaderElements = document.querySelectorAll('[data-custom-header-pagecard]');
          customHeaderElements.forEach((el) => el.remove());
        };
      } catch (error) {
        console.error('Error injecting header HTML:', error);
      }
    }
  }, [headerHtml]);

  const renderRowsContent = () => {
    if (pageType === 'external') {
      return (
        <HeroSection pageType={pageType}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            { theme.name === 'light' ? <ExternalPlaceholder /> : <ExternalPlaceholderDark /> }
          </div>
        </HeroSection>
      );
    }

    if (!pageData?.rows || pageData.rows.length === 0) {
      // Fallback to default hero section
      return (
        // <HeroSection pageType={pageType}>
        <div style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <BuilderZerostatePlaceholder width="370" height="250" />
        </div>
        // </HeroSection>

      );
    }

    // Helper to render a single element
    const renderElement = (element: PageElement) => {
      const config = element?.settings?.config;
      
      if (!config) return null;

      // Determine element type from accessKey
      const elementType = element.accessKey;
      const stableElementClass =
        (typeof config.elementClass === 'string' && config.elementClass.trim()) ||
        (typeof config.className === 'string' && config.className.trim()) ||
        createStableElementClass(elementType, element.id);
      const elementClassName = `${elementType}-element ${stableElementClass}`;

      // HEADING
      if (elementType === 'heading') {
        const content = config.htmlContent || config.content || '';
        const textAlign = config.textAlignment || 'left';
        const fontSize = config.fontSize || '18px';
        const fontWeight = resolveFontWeight(config.fontWeight);
        const elementFontFamily = resolvedFontFamily || config.fontFamily;
        const color = config.color || headingDefaultColor;

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <StyledText
              htmlTag={config.heading?.toLowerCase() || 'h2'}
              className={elementClassName}
              fontSize={fontSize}
              fontWeight={fontWeight}
              fontFamily={elementFontFamily}
              textColor={color}
              style={{ textAlign: textAlign as any }}
              data-element-id={element.id}
              data-element-type={elementType}
            >
              {content}
            </StyledText>
          </div>
        );
      }

      // PARAGRAPH
      if (elementType === 'paragraph') {
        const content = config.htmlContent || config.content || '';
        const textAlign = config.paragraphTextAlignment || 'left';
        const fontSize = config.paragraphFontSize || '13px';
        const fontWeight = resolveFontWeight(config.paragraphFontWeight);
        const elementFontFamily = resolvedFontFamily || config.paragraphFontFamily || config.fontFamily;
        const color = config.paragraphColor || paragraphDefaultColor;

        const isHtml = /<[^>]*>/.test(content);

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            {isHtml ? (
              <div 
                className='ql-editor' 
                style={{
                  color: color,
                  fontSize: fontSize,
                  pointerEvents: 'none',
                  cursor: 'default',
                }}
                dangerouslySetInnerHTML={{ __html: content }} 
              />
                ) :(
                <StyledText
                  htmlTag="p"
                  className={elementClassName}
                  fontSize={fontSize}
                  fontWeight={fontWeight}
                  fontFamily={elementFontFamily}
                  textColor={color}
                  style={{ textAlign: textAlign as any, lineHeight: '1.6' }}
                  data-element-id={element.id}
                  data-element-type={elementType}
                >
                  {content}
                </StyledText>
                )
            }
          </div>
        );
      }

      // SEPARATOR
      if (elementType === 'separator') {
        const separatorColor = config.separatorColor || separatorDefaultColor;
        const separatorHeight = config.separatorHeight || separatorDefaultHeight;
        const separatorType = config.separatorType || 'solid';

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <hr style={{
              border: 'none',
              borderTop: `${separatorHeight} ${separatorType} ${separatorColor}`,
              margin: '10px 0'
            }} className={elementClassName} data-element-id={element.id} data-element-type={elementType} />
          </div>
        );
      }

      // MEDIA (Image/Video)
      if (elementType === 'media') {
        const mediaType = config.mediaType || 'Image';
        const mediaTypePrefix = String(mediaType).toLowerCase();
        const mediaConfig: any = config;
        const mediaUrl = mediaTypePrefix === 'video'
          ? (
            mediaConfig.videoSignedUrl ||
            mediaConfig.signedUrl ||
            mediaConfig.videoPreview ||
            mediaConfig.videoExternalURL ||
            mediaConfig.videoUrl ||
            mediaConfig.videoURL
          )
          : (
            mediaConfig.imageSignedUrl ||
            mediaConfig.signedUrl ||
            mediaConfig.imagePreview ||
            mediaConfig.imageExternalURL ||
            mediaConfig.imageUrl ||
            mediaConfig.imageURL
          );
        const textAlignment = config.textAlignment || 'center';
        const linkURL = config.linkURL;
        const openNewTab = config.openNewTab;

        // If no media URL, show placeholder
        if (!mediaUrl) {
          return (
            <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '40px 20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #e9ecef'
              }}>
                <StyledText fontSize="14px" fontWeight={500} textColor="#6c757d" fontFamily={resolvedFontFamily}>
                  No media uploaded
                </StyledText>
              </div>
            </div>
          );
        }

        const resolvedMediaUrl = mediaUrl as string;
        const shouldRenderVideo = isYoutubeUrl(resolvedMediaUrl) || mediaType === 'Video' || mediaType === 'video' || isVideoFileUrl(resolvedMediaUrl);

        const mediaElement = !shouldRenderVideo ? (
          <img
            src={resolvedMediaUrl}
            alt="Media"
            className={elementClassName}
            data-element-id={element.id}
            data-element-type={elementType}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0'
            }}
          />
        ) : isYoutubeUrl(resolvedMediaUrl) ? (
          <iframe
            width="100%"
            height="300"
            src={getYoutubeEmbedUrl(resolvedMediaUrl) || resolvedMediaUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className={elementClassName}
            data-element-id={element.id}
            data-element-type={elementType}
            style={{
              maxWidth: '100%',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0'
            }}
          />
        ) : (
          <video
            controls
            className={elementClassName}
            data-element-id={element.id}
            data-element-type={elementType}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0'
            }}
          >
            <source src={resolvedMediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );

        return (
          <div key={element.id} style={{ marginBottom: '15px', textAlign: textAlignment as any }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            {linkURL ? (
              <a
                href={linkURL}
                target={openNewTab ? '_blank' : undefined}
                rel={openNewTab ? 'noopener noreferrer' : undefined}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                {mediaElement}
              </a>
            ) : (
              mediaElement
            )}
          </div>
        );
      }

      // FORM
      if (elementType === 'form') {
        if (config.formId) {
          return (
            <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
              <FormRenderComponent
                formId={String(config.formId)}
                formName={config.formName}
              />
            </div>
          );
        }

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '40px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #e9ecef'
            }}>
              <StyledText fontSize="14px" fontWeight={600} textColor="#0050ff" fontFamily={resolvedFontFamily}>
                Form Preview
              </StyledText>
              <StyledText fontSize="12px" fontWeight={400} textColor="#6c757d" fontFamily={resolvedFontFamily}>
                Form will be rendered here
              </StyledText>
            </div>
          </div>
        );
      }

      // SEARCH QUERY
      if (elementType === 'search_query') {
        if (config.searchQueryId && config.scope) {
          return (
            <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
              <SearchQueryRenderer
                searchQueryId={String(config.searchQueryId)}
                scope={config.scope}
                searchQueryName={config.searchQueryName}
                showPagination={false}
              />
            </div>
          );
        }

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '40px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #e9ecef'
            }}>
              <StyledText fontSize="14px" fontWeight={600} textColor="#0050ff" fontFamily={resolvedFontFamily}>
                Search Query Preview
              </StyledText>
              <StyledText fontSize="12px" fontWeight={400} textColor="#6c757d" fontFamily={resolvedFontFamily}>
                Search query will be rendered here
              </StyledText>
            </div>
          </div>
        );
      }

      // PUBLIC DIRECTORY
      if (elementType === 'public_directory') {
        if (config?.publicDirectorySetting?.scope) {
          const publicDirectoryQueryPayload = config.publicDirectorySetting.queryPayload ?? config.publicDirectorySetting;

          return (
            <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
              <PublicDirectory 
                scope={config.publicDirectorySetting.scope} 
                compactPreview 
                queryPayload={publicDirectoryQueryPayload}
              />
            </div>
          );
        }

        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '40px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #e9ecef'
            }}>
              <StyledText fontSize="14px" fontWeight={600} textColor="#0050ff" fontFamily={resolvedFontFamily}>
                Public Directory Preview
              </StyledText>
              <StyledText fontSize="12px" fontWeight={400} textColor="#6c757d" fontFamily={resolvedFontFamily}>
                No directory selected. Configure directory in settings.
              </StyledText>
            </div>
          </div>
        );
      }

      // CONTACT/ORGANIZATION SUMMARY
      if (elementType === 'contact_summary' || elementType === 'organization_summary') {
        return (
          <div key={element.id} style={{ marginBottom: '15px' }} className={elementClassName} data-element-id={element.id} data-element-type={elementType}>
            <SummaryCardComponent
              summaryType={elementType}
              onProfileClick={() => { }}
            />
          </div>
        );
      }

      // BUTTON
      if (elementType === 'button') {
        const buttonContent = config.content || 'Button';
        const buttonAlignment = config.buttonAlignment || 'left';
        const buttonBgColor = config.buttonColor || buttonDefaultColor;
        const buttonTextColor = config.buttonTextColor || buttonDefaultTextColor;
        const buttonLink = config.buttonLink;
        const openNewTab = config.openNewTab;

        const buttonNode = (
          <button
            className={elementClassName}
            data-element-id={element.id}
            data-element-type={elementType}
            style={{
              background: buttonBgColor,
              color: buttonTextColor,
              border: 'none',
              padding: '12px 30px',
              borderRadius: '6px',
              fontFamily: resolvedFontFamily,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {buttonContent}
          </button>
        );

        return (
          <div
            key={element.id}
            className={elementClassName}
            data-element-id={element.id}
            data-element-type={elementType}
            style={{
              marginBottom: '15px',
              display: 'flex',
              justifyContent: buttonAlignment === 'center' ? 'center' : buttonAlignment === 'right' ? 'flex-end' : 'flex-start'
            }}
          >
            {buttonLink ? (
              <a
                href={buttonLink}
                target={openNewTab ? '_blank' : undefined}
                rel={openNewTab ? 'noopener noreferrer' : undefined}
                style={{ textDecoration: 'none' }}
              >
                {buttonNode}
              </a>
            ) : (
              buttonNode
            )}
          </div>
        );
      }

      return null;
    };

    // Helper to render a single row
    const renderRow = (row: PageRow) => {
      if (!row.elements) return null;

      const columnCount = row.column || 1;
      const rowBackgroundColor =
        row.settings?.bgColor ||
        row.settings?.backgroundColor;
      const rowBackgroundImage =
        row.settings?.imageSignedUrl || row.settings?.imagePreview || row.settings?.imageUrl;
      const rowBackgroundOverlay = row.settings?.imageOverlayColor;
      const rowBackgroundOverlayOpacity = resolveOverlayOpacity(row.settings?.imageOverlayOpacity);
      const hasRowBackgroundImage = Boolean(rowBackgroundImage);
      const rowContainerStyle: React.CSSProperties = {
        backgroundColor: rowBackgroundColor,
        backgroundImage: rowBackgroundImage ? `url(${rowBackgroundImage})` : undefined,
        backgroundSize: rowBackgroundImage ? 'cover' : undefined,
        backgroundPosition: rowBackgroundImage ? 'center' : undefined,
        backgroundRepeat: rowBackgroundImage ? 'no-repeat' : undefined,
        borderRadius: rowBackgroundColor || rowBackgroundImage ? '8px' : undefined,
      };

      // If multi-column layout
      if (columnCount > 1) {
        // Group elements by belongsTo column
        const columnElements: { [key: number]: PageElement[] } = {};

        row.elements.forEach((element) => {
          const column = element.belongsTo || 1;
          if (!columnElements[column]) {
            columnElements[column] = [];
          }
          columnElements[column].push(element);
        });

        // Sort elements in each column by position
        Object.keys(columnElements).forEach((col) => {
          columnElements[parseInt(col)].sort((a, b) => (a.position || 0) - (b.position || 0));
        });

        return (
          <ColumnContainer
            columns={columnCount}
            key={`row-${row.id}`}
            $backgroundOverlay={rowBackgroundOverlay}
            $backgroundOverlayOpacity={rowBackgroundOverlayOpacity}
            $hasBackgroundImage={hasRowBackgroundImage}
            style={rowContainerStyle}
          >
            {Array.from({ length: columnCount }, (_, i) => {
              const colNum = i + 1;
              const colElements = columnElements[colNum] || [];
              return (
                <ColumnContent key={`col-${colNum}`}>
                  {colElements.map((element) => renderElement(element))}
                </ColumnContent>
              );
            })}
          </ColumnContainer>
        );
      }

      // Single column layout
      return (
        <HeroSection
          key={`row-${row.id}`}
          $backgroundOverlay={rowBackgroundOverlay}
          $backgroundOverlayOpacity={rowBackgroundOverlayOpacity}
          $hasBackgroundImage={hasRowBackgroundImage}
          style={rowContainerStyle}
        >
          {row.elements.map((element) => renderElement(element))}
        </HeroSection>
      );
    };

    // Render rows 0 and 1
    return (
      <>
        {pageData.rows[0] && renderRow(pageData.rows[0])}
        {pageData.rows[1] && renderRow(pageData.rows[1])}
      </>
    );
  };
  return (
    <CardContainer theme={theme}>
      {duplicatingPageId ? <div style={{
        position: 'absolute',
        top: -14,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
        gap: '6px',
        padding: '4px 8px',
        backgroundColor: hexToRgba('#F59E0B', 0.10),
        border: `1px solid ${hexToRgba('#F59E0B', 1)}`,
        borderRadius: '5px'
      }}>
        <StyledText textColor={theme.CorporateTheme.colors.headerColor} fontSize='10px' fontWeight={700}>
          Duplicating...
        </StyledText>
      </div> : null}
      {/* Window Header */}
      <WindowHeader theme={theme}>
        <div className='d-flex align-items-center gap-1'>
          <Dot color="#ef4444" />
          <Dot color="#f59e0b" />
          <Dot color="#10b981" />
        </div>
        <Bar />
        <div />
      </WindowHeader>

      {/* Hero Content - Render rows with multi-column support */}
      <div
        ref={contentWrapperRef}
        data-pagecard-content
        style={{
          height: '210px',
          overflow: 'hidden',
          fontFamily: resolvedFontFamily
        }}
      >
        {renderRowsContent()}
      </div>

      {/* Footer */}
      <FooterSection>
        <div className='d-flex align-items-center justify-content-between gap-2'>
          <PageInfo>
            <div className='d-flex align-items-center gap-2'>
              <StyledText fontWeight={600} fontSize='16px' textColor={theme.CorporateTheme.colors.primary}>{pageName}&nbsp;<Badge borderColor="none" padding='5px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.2)} color={theme.CorporateTheme.colors.primary} fontSize='10px' fontStyle='italic' fontWeight={600} text={capitalizeFirstLetter(pageType)} borderRadius='3px' />
              </StyledText>
            </div>
            <StyledText fontWeight={500} fontSize='12px' textColor={theme.CorporateTheme.colors.textSecondary}>Published Date: {moment(publishedDate).format('DD MMMM, YYYY')}</StyledText>
          </PageInfo>
          <div className='d-flex align-items-center gap-2'>

            {/* <OpitonsIcon size={26} rotation={90} color={theme.CorporateTheme.colors.headerColor} onClick={onMoreClick} /> */}
            {/* <Badge borderColor={theme.CorporateTheme.colors.melonYellow} padding='10px 18px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.1)} color={theme.CorporateTheme.colors.headerColor} fontSize='12px' fontWeight={600} text={TextWithoutDashes(visibility)} borderRadius='28px' /> */}

            <DropdownButton
              icon={<OpitonsIcon color={theme.CorporateTheme.colors.headerColor} size={26} rotation={90} />}
              iconPosition="right"
              buttonLabel=""
              align="right"
              removeborder
              menuFontWeight={500}
              items={[
                ...(pageType !== "external"
                  ? [{ label: "Edit Page", onClick: onEditPageSetting, icon: <EditIcon color={theme.CorporateTheme.colors.headerColor} size={18} /> }]
                  : []),
                { label: "Edit Page Settings", onClick: onEditPage, icon: <EditIcon color={theme.CorporateTheme.colors.headerColor} size={18} /> },
                { label: "Duplicate Page", onClick: onDuplicate, icon: <CopyIcon color={theme.CorporateTheme.colors.headerColor} size={18} /> },
                ...(!isStandard && pageData?.statusId !== Status.Published
                  ? [{ label: "Delete Page", onClick: onDelete, icon: <DeleteIcon color={theme.CorporateTheme.colors.headerColor} size={18} /> }
                  ]
                  : []),
                ...(pageData?.statusId === Status.Published
                  ? [
                    { label: "Copy Page URL", onClick: onCopyPageURL, icon: <URLIcon color={theme.CorporateTheme.colors.headerColor} size={18} /> }
                  ]
                  : [])
              ]}
            />
          </div>
        </div>
        <StyledHorizontalLine theme={theme} className='my-2' />
        <div className='d-flex align-items-center justify-content-end gap-2 me-3'>
          <Badge borderColor={theme.CorporateTheme.colors.melonYellow} padding='10px 18px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.1)} color={theme.CorporateTheme.colors.headerColor} fontSize='12px' fontWeight={600} text={TextWithoutDashes(visibility)} borderRadius='28px' />
          <Badge borderColor={theme.CorporateTheme.colors.melonYellow} padding='10px 18px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.1)} color={theme.CorporateTheme.colors.headerColor} fontSize='12px' fontWeight={600} text={isStandard ? "System Page" : "Custom Page"} borderRadius='28px' />
        </div>
      </FooterSection>
    </CardContainer >
  );
};

export default PageCard;